import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { styled } from '@mui/material/styles'
import { DateField } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { useDispatch } from 'react-redux'
import { SearchIcon } from '../mui/Icons'
import { Box } from '../mui/Core'
import { useTheme } from '../mui/Styles/index'
import Constants from '../../constants/en.json'
import { inputFormattingConstants } from '../../constants/input-formatting-constants'
import { setSnackbar } from '../GenericSnackbar/generic-snackbar-slice'

const Search = styled(Box)(({ theme }) => ({
  height: 36,
  minWidth: '300px',
  position: 'relative',
  backgroundColor: 'none',
  marginRight: theme.spacing(0),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: { marginLeft: theme.spacing(0), width: 'auto' },
}))

const SearchIconWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

const DateDashSpacer = styled(Box)(() => ({
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

const searchIconStyle = {
  fontSize: '28px',
}

function DateRangeSearch({ returnSearch }) {
  const theme = useTheme()
  const dispatch = useDispatch()
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [searchedDates, setSearchedDates] = useState({ startDate: null, endDate: null })

  const changeDate = (value, name) => {
    if (name === 'startDate') {
      setStartDate(value)
    }
    if (name === 'endDate') {
      setEndDate(value)
    }
  }

  const isInvalidDateRange = (formattedStartDate, formattedEndDate) => {
    const startYear = formattedStartDate.split('/')?.at(-1)
    const endYear = formattedEndDate.split('/')?.at(-1)
    const isInvalid =
      formattedStartDate === 'Invalid Date' ||
      formattedEndDate === 'Invalid Date' ||
      parseInt(startYear, 10) < 1900 ||
      parseInt(endYear, 10) < 1900

    if (isInvalid) return true
    return false
  }

  const showInvalidDateError = () => {
    dispatch(
      setSnackbar({
        message: Constants.orders.INVALID_DATE_PERIOD_MESSAGE,
        severity: 'error',
        action: true,
        autoHideDuration: 3000,
      }),
    )
  }

  const handleKeyPress = (e) => {
    if (e.key !== 'Enter') return

    if (!startDate && !endDate) {
      if (searchedDates.startDate || searchedDates.endDate) {
        setSearchedDates({ startDate: null, endDate: null })
        returnSearch('', '')
      }
      return
    }

    if (startDate && endDate) {
      const formattedStartDate = dayjs(startDate).format(
        inputFormattingConstants.dateFormats.MMDDYYYY,
      )
      const formattedEndDate = dayjs(endDate).format(inputFormattingConstants.dateFormats.MMDDYYYY)
      if (isInvalidDateRange(formattedStartDate, formattedEndDate)) {
        showInvalidDateError()
        return
      }

      const isSameDatePeriodSearch =
        searchedDates.startDate === formattedStartDate && searchedDates.endDate === formattedEndDate
      if (isSameDatePeriodSearch) return

      setSearchedDates({ startDate: formattedStartDate, endDate: formattedEndDate })
      returnSearch(formattedStartDate, formattedEndDate)
      return
    }
    showInvalidDateError()
  }

  const styledBorder = {
    backgroundColor: theme.palette.common.white,
    borderColor: theme.palette.grey[700],
    border: 1,
    borderRadius: theme.shape.borderRadius,
    height: '36px',
    alignItems: 'center',
    display: 'flex',
    ':focus-within': {
      border: '2px solid',
      borderColor: `${theme.palette.primary.main} !important`,
    },
  }

  const startDateFieldStyle = {
    margin: theme.spacing(0.5, 2, 0, 5),
    width: '200px',
    height: '35px',
    backgroundColor: 'transparent',
    '& .MuiInputBase-root': {
      background: 'none',
      fontSize: '16px',
      fontFamily: 'AtlasGroteskRegular',
      '& fieldset': {
        border: 'none',
      },
    },
    '& .MuiInputBase-input': {
      padding: theme.spacing(0),
      paddingLeft: '20px',
      width: '100%',
      [theme.breakpoints.up('md')]: {
        width: '20ch',
      },
    },
  }

  const startDateLabelStyle = {
    marginLeft: '-30px',
    color: theme.palette.common.black,
  }

  const endDateLabelStyle = {
    marginLeft: '-20px',
    color: theme.palette.common.black,
  }

  const endDateFieldStyle = {
    margin: theme.spacing(0.5, 2, 0, 8),
    width: '190px',
    height: '35px',
    backgroundColor: 'transparent',
    '& .MuiInputBase-root': {
      background: 'none',
      fontSize: '16px',
      fontFamily: 'AtlasGroteskRegular',
      '& fieldset': {
        border: 'none',
      },
    },
    '& .MuiInputBase-input': {
      padding: theme.spacing(0),
      width: '100%',
      [theme.breakpoints.up('md')]: {
        width: '20ch',
      },
    },
  }

  return (
    <Box display='flex' sx={styledBorder}>
      <Search>
        <SearchIconWrapper>
          <SearchIcon sx={searchIconStyle} />
        </SearchIconWrapper>
        <DateField
          name='startDate'
          data-testid='startDate'
          format={inputFormattingConstants.dateFormats.MMDDYYYY}
          size='small'
          label='Date Generated (From)'
          slotProps={{ inputLabel: { shrink: true, style: startDateLabelStyle } }}
          value={startDate}
          maxDate={dayjs('9999-12-31')}
          minDate={dayjs('1900-01-01')}
          onChange={(newValue) => {
            changeDate(newValue, 'startDate')
          }}
          onKeyDown={(e) => handleKeyPress(e)}
          sx={startDateFieldStyle}
        />
        <DateDashSpacer> - </DateDashSpacer>
        <DateField
          name='endDate'
          data-testid='endDate'
          format={inputFormattingConstants.dateFormats.MMDDYYYY}
          size='small'
          margin='dense'
          label='Date Generated (To)'
          slotProps={{ inputLabel: { shrink: true, style: endDateLabelStyle } }}
          value={endDate}
          maxDate={dayjs('9999-12-31')}
          minDate={dayjs('1900-01-01')}
          onChange={(newValue) => {
            changeDate(newValue, 'endDate')
          }}
          onKeyDown={(e) => handleKeyPress(e)}
          sx={endDateFieldStyle}
        />
      </Search>
    </Box>
  )
}

DateRangeSearch.propTypes = {
  returnSearch: PropTypes.func.isRequired,
}

export default DateRangeSearch
